import type React from 'react';
import { forwardRef, useState } from 'react';
import { DateLikeInput } from './date-input';
import type { RangeLikeProps } from './types';

export interface DateTimeInputProps extends RangeLikeProps {
  /**
   * The earliest date time to accept, in the `YYYY-MM-DDTHH:mm` format.
   */
  min?: string;

  /**
   * The latest date time to accept, in the `YYYY-MM-DDTHH:mm` format.
   */
  max?: string;

  /**
   * What type of information to autocomplete in the input.
   */
  autoComplete?: string;

  /**
   * Default value of an uncontrolled input.
   */
  defaultValue?: string;

  /**
   * Value of the input.
   *
   * Makes the input controlled.
   */
  value?: string;

  /**
   * Fires when the input’s value is changed.
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
}

type ControlledProps = {
  /**
   * Value of the input.
   *
   * Makes the input controlled.
   */
  value: string;

  /**
   * Fires immediately when the input’s value is changed by the user (for example, it fires on every keystroke).
   */
  onChange: React.ChangeEventHandler<HTMLInputElement>;

  defaultValue?: never;
};

type UncontrolledProps = {
  /**
   * Default value of an uncontrolled input.
   */
  defaultValue?: string;

  /**
   * Fires immediately when the input’s value is changed by the user (for example, it fires on every keystroke).
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;

  value?: never;
};

/**
 * @deprecated Use `DateTimeInputProps` instead.
 */
export type DateTimeInputUncontrolledProps = Omit<
  DateTimeInputProps,
  'defaultValue' | 'onChange' | 'value'
> &
  UncontrolledProps;

/**
 * @deprecated Use `DateTimeInputProps` instead.
 */
export type DateTimeInputControlledProps = Omit<
  DateTimeInputProps,
  'defaultValue' | 'onChange' | 'value'
> &
  ControlledProps;

/**
 * Lets the users select a date from the native calendar dropdown.
 * Should be used when it is helpful for the user to see what weekday a selected date is.
 *
 * Browser compatibility:
 * - https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/datetime-local#browser_compatibility
 * - As of 24 Sep 2024, Safari and Firefox do not show the visual date picker, but the functionality is still available via the keyboard.
 * - Be careful with this component on pages with broader public audiences.
 */
/**
 * TelInput should be used whenever the user needs to enter a phone number.
 *
 * It's identical to a TextInput, but with appropriate autocomplete and mobile
 * keyboard attributes.
 */
export const DateTimeInput = forwardRef<HTMLInputElement, DateTimeInputProps>(
  function TelInput(
    props: DateTimeInputProps,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) {
    return <DateLikeInput {...props} type="datetime-local" ref={ref} />;
  },
);
